import { useQueryClient } from '@tanstack/react-query'
import { Switch } from '@/common/ui/switch'
import { Badge } from '@/common/ui/badge'
import { Loader2, CheckCircle2, XCircle } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'
import showErrorToast from '@/common/utils/showErrorToast'
import useMutationUpdatePaymentGateway from '@/modules/bank/api/hooks/useMutationUpdatePaymentGateway'
import { BankQueryKeys } from '@/modules/bank/keys'
import LoadingOverlay from '@/common/components/LoadingOverlay'
import { cn } from '@/common/lib/utils'

import type {
	GetPaymentGateWayResponseData,
	PaymentChannelTab,
} from '@/modules/bank/type'

interface TogglePaymentGatewayStatusProps {
	row: { original: GetPaymentGateWayResponseData }
	channel?: PaymentChannelTab
}

export default function TogglePaymentGatewayStatus({
	row,
	channel,
}: TogglePaymentGatewayStatusProps) {
	const { mutateAsync: updatePaymentGateway, isPending } =
		useMutationUpdatePaymentGateway()
	const queryClient = useQueryClient()
	const [isLoading, setIsLoading] = useState(false)
	const isActive = row.original.isActive

	const handleChange = async (checked: boolean) => {
		setIsLoading(true)
		try {
			await updatePaymentGateway({
				payload: {
					id: row.original.id,
					isActive: checked,
					...(channel ? { channel: channel as string } : {}),
				},
			})
			queryClient.invalidateQueries({
				queryKey: [BankQueryKeys.PaymentGateways],
			})
			toast.success('อัพเดทสถานะสำเร็จ')
		} catch (error) {
			showErrorToast(error as Error)
		} finally {
			setIsLoading(false)
		}
	}

	return (
		<>
			<LoadingOverlay
				open={isPending || isLoading}
				message="กำลังอัพเดทสถานะ ธนาคารออโต้..."
			/>
			<div className="flex items-center gap-3">
				<Switch
					checked={isActive}
					onCheckedChange={handleChange}
					disabled={isLoading || isPending}
					className="data-[state=checked]:bg-green-600"
				/>
				{isLoading || isPending ? (
					<Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
				) : (
					<Badge
						variant={isActive ? 'default' : 'secondary'}
						className={cn(
							'flex items-center gap-1.5 px-2 py-0.5',
							isActive
								? 'bg-green-100 text-green-800 dark:bg-green-950 dark:text-green-200'
								: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200',
						)}
					>
						{isActive ? (
							<CheckCircle2 className="h-3.5 w-3.5" />
						) : (
							<XCircle className="h-3.5 w-3.5" />
						)}
						<span className="text-xs font-medium">
							{isActive ? 'เปิดใช้งาน' : 'ปิดใช้งาน'}
						</span>
					</Badge>
				)}
			</div>
		</>
	)
}
